import { CloudOff, Loader2, Check, RefreshCw, WifiOff } from 'lucide-react'
import type { AutoSaveStatus } from './useAutoSave'

interface AutoSaveIndicatorProps {
  status: AutoSaveStatus
  retryCount?: number
  lastError?: string | null
  onRetry?: () => void
}

/**
 * Compact indicator showing auto-save status.
 * Renders inline — place it in headers or footers.
 * Shows retry button on persistent errors.
 */
export function AutoSaveIndicator({ status, retryCount = 0, lastError, onRetry }: AutoSaveIndicatorProps) {
  if (status === 'idle') return null

  return (
    <div className="flex items-center gap-1.5 text-xs transition-opacity duration-300">
      {status === 'saving' && (
        <>
          <Loader2 size={12} className="animate-spin text-primary" />
          <span className="text-[var(--text-muted)]">Saving...</span>
        </>
      )}
      {status === 'saved' && (
        <>
          <Check size={12} className="text-success" />
          <span className="text-success">Saved</span>
        </>
      )}
      {status === 'retrying' && (
        <>
          <RefreshCw size={12} className="animate-spin text-warning" />
          <span className="text-warning">Retrying{retryCount > 0 ? ` (${retryCount})` : ''}...</span>
        </>
      )}
      {status === 'error' && (
        <div className="flex items-center gap-2">
          <CloudOff size={12} className="text-danger" />
          <span className="text-danger" title={lastError || undefined}>Save failed</span>
          {onRetry && (
            <button
              onClick={onRetry}
              className="px-1.5 py-0.5 text-[10px] font-medium text-danger border border-danger/30 rounded hover:bg-danger/10 transition-colors"
            >
              Retry
            </button>
          )}
        </div>
      )}
      {status === 'offline' && (
        <>
          <WifiOff size={12} className="text-warning" />
          <span className="text-warning">Offline — changes will save when reconnected</span>
        </>
      )}
    </div>
  )
}
